import type { ReactNode } from "react"

type VerdictTone = "good" | "warn" | "neutral"

interface VerdictReason {
  label: string
  tone: "positive" | "negative"
}

interface VerdictCardProps {
  /** Tone controls the gradient and accent color */
  tone: VerdictTone
  /** Icon rendered in the top-left tile */
  icon: ReactNode
  /** Tiny uppercase label, e.g. "Our Verdict" */
  eyebrow: string
  /** Short accent subtitle shown next to the icon tile */
  subtitle?: string
  /** Big headline label (e.g. "Repair It", "Replace It") */
  headline: string
  /** One-line secondary text under the headline */
  caption?: string
  /** Score from 0-100 for the confidence bar (optional) */
  score?: number
  /** Labels for the three bar zones */
  scoreLabels?: [string, string, string]
  /** Up to 5 ranked reasons for the verdict */
  reasons?: VerdictReason[]
  /** Additional classes on outer card */
  className?: string
}

const TONE_CLASSES: Record<VerdictTone, { bg: string; accent: string }> = {
  good: {
    bg: "bg-gradient-to-br from-green-600 to-emerald-700",
    accent: "text-green-200",
  },
  warn: {
    bg: "bg-gradient-to-br from-[#ff9951] to-[#e06a1a]",
    accent: "text-amber-100",
  },
  neutral: {
    bg: "bg-gradient-to-br from-[#0469f0] to-[#0053c4]",
    accent: "text-blue-200",
  },
}

/**
 * VerdictCard — decision-tree result card that presents a binary/ternary
 * verdict with a confidence bar and top-weighted reasons. Used anywhere a
 * tool's output is a recommendation ("Repair", "Replace", "Worth it", "Skip")
 * rather than a raw number.
 */
export function VerdictCard({
  tone,
  icon,
  eyebrow,
  subtitle,
  headline,
  caption,
  score,
  scoreLabels,
  reasons,
  className = "",
}: VerdictCardProps) {
  const t = TONE_CLASSES[tone]
  return (
    <div
      className={`${t.bg} rounded-xl p-6 md:p-8 text-white shadow-[0_8px_24px_rgba(0,25,69,0.12)] relative overflow-hidden ${className}`}
    >
      <div className="absolute top-0 right-0 w-48 h-48 rounded-full bg-white/10 blur-2xl -translate-y-12 translate-x-12" />
      <div className="relative">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-11 h-11 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center border border-white/20">
            {icon}
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/70 font-bold">{eyebrow}</p>
            {subtitle && <p className={`text-sm ${t.accent} font-semibold`}>{subtitle}</p>}
          </div>
        </div>

        <div className="text-6xl md:text-7xl font-black tracking-tighter leading-none mb-2">
          {headline}
        </div>
        {caption && <p className="text-base text-white/80 mb-6">{caption}</p>}

        {typeof score === "number" && (
          <div className="mb-5">
            {scoreLabels && (
              <div className="flex justify-between text-[11px] font-semibold text-white/70 mb-1.5">
                <span>{scoreLabels[0]}</span>
                <span>{scoreLabels[1]}</span>
                <span>{scoreLabels[2]}</span>
              </div>
            )}
            <div className="h-3 bg-white/15 rounded-full overflow-hidden relative">
              <div className="absolute inset-y-0 left-0 bg-white/25" style={{ width: "35%" }} />
              <div className="absolute inset-y-0 left-[35%] bg-white/30" style={{ width: "25%" }} />
              <div className="absolute inset-y-0 right-0 bg-white/25" style={{ width: "40%" }} />
              <div
                className="absolute top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-white border-2 border-gray-900 shadow-md transition-all"
                style={{ left: `calc(${Math.min(100, Math.max(0, score))}% - 10px)` }}
              />
            </div>
          </div>
        )}

        {reasons && reasons.length > 0 && (
          <div className="space-y-2">
            <p className="text-[10px] uppercase tracking-widest text-white/60 font-bold mb-1">Why</p>
            {reasons.map((r, i) => (
              <div key={i} className="flex items-start gap-2 text-sm">
                <span className="w-4 h-4 flex-shrink-0 mt-0.5 text-white/80">
                  {r.tone === "negative" ? "▼" : "▲"}
                </span>
                <span className="text-white/90">{r.label}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export type { VerdictTone, VerdictReason }
